import { Log } from '@apparel/core/log';
import { parse } from '@lib/parse';
import ColorHash from 'color-hash';
import { each } from 'lodash';
import { getLuminance } from 'polished';
import { css } from 'styled-components';
import { between, classNames } from '../utils';
import { unit, unitStrict } from './unit';

export type WrapType = 'wrap' | 'nowrap';

/**
 * Calculate flex
 * Logic:
 * - Percent: 0 <= value <= 0.99
 * - Col number: 1 <= value <= 12
 * - Pixel number: 12 < value
 * @example
 * calculateFlex(0.5) // result '50%';
 * calculateFlex(0.33) // result '33%';
 * calculateFlex(3) // result '25%';
 * calculateFlex(6) // result '50%';
 * calculateFlex(12) // result '100%';
 * calculateFlex(300) // result '300px';
 * calculateFlex(300) // result '300px';
 * calculateFlex('calc(100% - 200px)') // result 'calc(100% - 200px)';
 *
 * @param value
 * @returns {string}
 */
export function calculateFlex(value): string {
    value = parse(value);

    if (typeof value === 'number') {
        let _unit = '%';
        value = Math.abs(value);

        if (value < 1) {
            value = value * 100;
        } else if (value >= 1 && value <= 12) {
            value = (100 * value) / 12;
        } else {
            _unit = 'px';
        }

        return `${unitStrict(value, _unit)}`;
    } else if (typeof value === 'string') {
        return value;
    }

    return '';
}

/**
 * Get wrap value
 * @param value
 * @returns {string}
 */
export function getWrap(value: boolean | WrapType): WrapType {
    value = parse(value);

    // @ts-ignore
    return value || value === 'wrap' ? 'wrap' : 'nowrap';
}

const FLEX_ALIAS = {
    left: 'flex-start',
    start: 'flex-start',
    top: 'flex-start',

    end: 'flex-end',
    right: 'flex-end',
    bottom: 'flex-start',

    inherit: 'inherit',
    initial: 'initial',
    center: 'center',
    baseline: 'baseline',
    stretch: 'stretch',
    'space-around': 'space-around',
    'space-between': 'space-between',

    'flex-end': 'flex-end',
    'flex-start': 'flex-start',
};

export type FlexDirection = 'column' | 'row' | 'column-reverse' | 'row-reverse';
export type JustifyAlignValue = keyof typeof FLEX_ALIAS;

/**
 * Get Justify or Align right value
 * @param value
 * @returns {*}
 */
export function getJustifyAlign(value: any | JustifyAlignValue): JustifyAlignValue {
    value = parse(value);

    if (value && typeof value === 'string') {
        value = value.trim();
    }

    return FLEX_ALIAS.hasOwnProperty(value) ? FLEX_ALIAS[value] : value;
}

/**
 * Log a property style
 * @param value
 * @param args
 * @returns {*}
 */
export const logProp = (value: any, ...args): any => {
    Log.debug('Property style: ', value, ...args);
    return value;
};

/**
 * Get Gap value
 * @param theme
 * @param gap
 * @param _negative
 * @returns {string}
 */
export const getGap = ({ theme, gap, _negative }: any): string => {
    const _gap = typeof gap !== 'undefined' ? gap : theme.container.gap;
    return `${_negative ? (parseInt(_gap, 0) !== 0 ? '-' : '') : ''}${unit(_gap)}`;
};

/**
 * Get gap (eg: gap={12} => '12px')
 * @param theme
 * @param gap
 * @returns {string}
 */
export const gap = ({ theme, gap }): string => getGap({ theme, gap });

/**
 * Get gap but negative (eg: gap={12} => '-12px')
 * @param theme
 * @param gap
 * @returns {string}
 */
export const negativeGap = ({ theme, gap }): string => getGap({ theme, gap, _negative: true });

/**
 * Create Media query styles
 * @example app/Layout/Row.jsx:17
 * @param attrs
 * @param style
 * @returns {any}
 */
export const createMediaQueryStyles = (attrs, style) => {
    return (query, point) => {
        return (props): any => {
            let canRender = false;

            each(attrs, (key) => {
                if (props.hasOwnProperty(key) && props[key]) {
                    if (typeof props[key] === 'object' && props[key].hasOwnProperty(point) && props[key][point]) {
                        canRender = true;
                        return false;
                    }
                }
            });

            if (!canRender) {
                return '';
            }

            return style({ query, point });
        };
    };
};

/**
 * Cache store for hash color
 * @type {{}}
 * @private
 */
const __COLORS__ = {};

/**
 * Color cache helper
 * @usage https://github.com/zenozeng/color-hash
 * @type {ColorHash}
 */
export const colorHash = new ColorHash();

/**
 * ColorHash
 * @param children
 * @param method
 * @returns {*}
 */
export const hashColor = (children, method = 'hex'): string => {
    if (!__COLORS__.hasOwnProperty(children)) {
        __COLORS__[`${children}@${method}`] = colorHash[method](children);
    }
    return __COLORS__[`${children}@${method}`];
};

export const magicTransition = 'cubic-bezier(0.4, 0, 0.2, 1)';

export const imageZoom = css`
    will-change: transform, opacity;
    transition: opacity 250ms ${magicTransition}, transform 250ms ${magicTransition};
    opacity: 0;
    transform: scale(0.7, 0.7);

    &.active {
        opacity: 1;
        transform: scale(1, 1);
    }
`;

/**
 *
 * @param type
 * @returns {*}
 */
export const viewPort = (
    type: 'inner' | 'outer' = 'inner',
): {
    width: number;
    height: number;
} =>
    typeof window !== 'undefined'
        ? {
              width: window[`${type}Width`],
              height: window[`${type}Height`],
          }
        : {
              width: 65535,
              height: 65535,
          };

/**
 * Readable color function
 * @param color
 * @param lightReturnColor
 * @param darkReturnColor
 */
export function readableColor(color: any, lightReturnColor = '#000', darkReturnColor = '#fff') {
    return getLuminance(color) > 0.287 ? lightReturnColor : darkReturnColor;
}

export { classNames, between };
